import { useMemo, useState } from 'preact/hooks';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { api } from '../../lib/api.js';
import { Button } from '../../components/Button.jsx';
import { PrinterEditModal } from '../printers/PrinterEditModal.jsx';
import { EquipmentEditModal } from '../equipment/EquipmentEditModal.jsx';
import { STATUS_LABEL, STATUS_PILL, TEAL, buildMachineList } from './machines-shared.js';

const StatusPill = ({ status }) => (
  <span class={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_PILL[status] ?? STATUS_PILL.active}`}>
    <span class="block h-1.5 w-1.5 rounded-full bg-current" />
    {STATUS_LABEL[status] ?? status}
  </span>
);

const TYPE_LABEL = { printer: 'Printer', equipment: 'Equipment' };

const MachineCard = ({ machine, isAdmin, onEdit, onDelete }) => {
  const body = (
    <>
      <div class="mb-2.5 flex items-center justify-between">
        <StatusPill status={machine.status} />
        <span
          class="text-[10px] font-semibold uppercase tracking-eyebrow"
          style={{ color: machine.categoryColor || TEAL }}
        >
          {machine.categoryName ?? TYPE_LABEL[machine.type]}
        </span>
      </div>
      <p class="m-0 mb-1 font-serif text-[18px] font-medium tracking-tight text-text-primary">{machine.name}</p>
      <p class="m-0 text-[12px] font-medium text-text-tertiary">
        <span class="text-text-muted">{TYPE_LABEL[machine.type]}</span>
        {machine.model && <span class="ml-2">· {machine.model}</span>}
        {machine.location && <span class="ml-2">· {machine.location}</span>}
        {machine.ipAddress && <span class="ml-2 font-mono text-text-body">{machine.ipAddress}</span>}
      </p>
    </>
  );

  const classes = 'relative block rounded-lg border border-border-subtle bg-bg-card p-4 text-left transition hover:border-border-strong hover:bg-bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand';

  return (
    <div class="relative">
      {machine.type === 'printer' ? (
        <a href={machine.href} class={classes}>{body}</a>
      ) : (
        <button type="button" onClick={() => onEdit(machine)} class={`w-full ${classes}`}>{body}</button>
      )}
      {isAdmin && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(machine); }}
          title="Delete"
          class="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full border border-border-strong bg-bg-base text-text-tertiary hover:border-ink-magenta hover:text-ink-magenta"
        >
          ×
        </button>
      )}
    </div>
  );
};

export const MachinesPage = () => {
  const { data: printers, refetch: refetchPrinters } = useApi('/printers?includeInactive=true');
  const { data: equipment, refetch: refetchEquipment } = useApi('/equipment');
  const { data: categories, refetch: refetchCategories } = useApi('/equipment/categories');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [categoryFilter, setCategoryFilter] = useState('all');
  const [addPrinterOpen, setAddPrinterOpen] = useState(false);
  const [addEquipmentOpen, setAddEquipmentOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);

  const machines = useMemo(() => buildMachineList(printers, equipment), [printers, equipment]);

  const refetchAll = () => { refetchPrinters(); refetchEquipment(); refetchCategories(); };

  const counts = useMemo(() => {
    const acc = { all: 0 };
    machines.forEach((m) => {
      acc.all += 1;
      const key = m.categoryId ?? 'none';
      acc[key] = (acc[key] ?? 0) + 1;
    });
    return acc;
  }, [machines]);

  const filtered = useMemo(() => {
    if (categoryFilter === 'all') return machines;
    if (categoryFilter === 'none') return machines.filter((m) => m.categoryId == null);
    return machines.filter((m) => String(m.categoryId) === categoryFilter);
  }, [machines, categoryFilter]);

  const handleDelete = async (machine) => {
    if (!window.confirm(`Delete "${machine.name}"? This can't be undone.`)) return;
    try {
      await api.delete(machine.type === 'printer' ? `/printers/${machine.id}` : `/equipment/${machine.id}`);
      refetchAll();
    } catch (err) {
      alert(err.message ?? 'Failed to delete');
    }
  };

  const loading = printers == null || equipment == null;

  return (
    <div>
      <section class="mb-6 flex items-start justify-between gap-4">
        <div>
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: TEAL }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: TEAL }}>
              Machines
            </span>
          </div>
          <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">
            All machines
          </h1>
          <p class="m-0 text-sm text-text-secondary">
            Every printer and piece of equipment in one place — add, tag, or remove any machine.
          </p>
        </div>
        <div class="flex gap-2">
          <Button variant="secondary" onClick={() => setAddPrinterOpen(true)}>+ Printer</Button>
          <Button variant="secondary" onClick={() => setAddEquipmentOpen(true)}>+ Equipment</Button>
        </div>
      </section>

      <div class="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setCategoryFilter('all')}
          class={`rounded-full border px-3 py-1 text-[11px] font-semibold ${categoryFilter === 'all' ? 'border-brand bg-brand/[0.12] text-brand' : 'border-border-strong text-text-tertiary hover:bg-bg-card'}`}
        >
          All · {counts.all ?? 0}
        </button>
        {(categories ?? []).map((c) => (
          counts[c.id] > 0 && (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategoryFilter(String(c.id))}
              class={`rounded-full border px-3 py-1 text-[11px] font-semibold ${categoryFilter === String(c.id) ? 'border-brand bg-brand/[0.12] text-brand' : 'border-border-strong text-text-tertiary hover:bg-bg-card'}`}
            >
              {c.name} · {counts[c.id]}
            </button>
          )
        ))}
        {counts.none > 0 && (
          <button
            type="button"
            onClick={() => setCategoryFilter('none')}
            class={`rounded-full border px-3 py-1 text-[11px] font-semibold ${categoryFilter === 'none' ? 'border-brand bg-brand/[0.12] text-brand' : 'border-border-strong text-text-tertiary hover:bg-bg-card'}`}
          >
            No category · {counts.none}
          </button>
        )}
      </div>

      {loading && <p class="text-sm text-text-tertiary">Loading…</p>}

      {filtered.length > 0 && (
        <div class="grid grid-cols-1 gap-2.5 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((m) => (
            <MachineCard
              key={`${m.type}-${m.id}`}
              machine={m}
              isAdmin={isAdmin}
              onEdit={setEditingEquipment}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
      {!loading && filtered.length === 0 && (
        <div class="surface-card p-6 text-center">
          <p class="m-0 font-serif text-lg font-medium text-text-primary">No machines yet.</p>
          <p class="m-0 mt-1 text-sm text-text-secondary">
            Add a printer or a piece of equipment above.
          </p>
        </div>
      )}

      <PrinterEditModal
        open={addPrinterOpen}
        onClose={() => setAddPrinterOpen(false)}
        onSaved={() => { setAddPrinterOpen(false); refetchAll(); }}
      />
      <EquipmentEditModal
        open={addEquipmentOpen}
        onClose={() => setAddEquipmentOpen(false)}
        onSaved={() => { setAddEquipmentOpen(false); refetchAll(); }}
      />
      <EquipmentEditModal
        open={!!editingEquipment}
        equipment={editingEquipment}
        onClose={() => setEditingEquipment(null)}
        onSaved={() => { setEditingEquipment(null); refetchAll(); }}
      />
    </div>
  );
};
